import React, { useState, useRef, useEffect } from 'react';
import Svg from 'components/utils/Svg/Svg';
import { imageLinks } from 'assets/images';
import { useLanguage } from 'contexts/LanguageContext';
import './LanguageSelector.scss';

interface LanguageSelectorProps {
    className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '' }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { currentLanguage, languages, setLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // New state for cycling text
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    
    // Translations for "Choose your preferred language"
    const languageTexts = [
        { code: 'english', text: 'Choose your preferred language' },
        // { code: 'yoruba', text: 'Yan ede ti o fe' },
        { code: 'hausa', text: 'Zaɓi harshen da kake so' },
        // { code: 'igbo', text: 'Họrọ asụsụ ị chọrọ' },
        // { code: 'pidgin', text: 'Choose di language wey you like' }
    ];

    useEffect(() => {
        if (currentLanguage === null) {
            setIsOpen(true);
            setIsActive(true);
            // hasShownToast.current = true;
            // showToast("Click here to change language", {
            //     targetRef: buttonRef as React.RefObject<HTMLElement>,
            //     placement: 'top',
            //     offset: 8,
            //     showOverlay: true,
            //     type: 'info'
            // });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // New useEffect for cycling text animation
    useEffect(() => {
        if (!isOpen) return;
        
        const interval = setInterval(() => {
            setIsTransitioning(true);
            
            setTimeout(() => {
                setCurrentTextIndex((prevIndex) => 
                    (prevIndex + 1) % languageTexts.length
                );
                setIsTransitioning(false);
            }, 400); // Half of the 0.8s transition
        }, 3500); // Much slower - change every 3.5 seconds
        
        return () => clearInterval(interval);
    }, [isOpen, languageTexts.length]);

    const toggleDropdown = () => {
        setIsOpen((prev) => !prev);
        setIsActive((prev) => !prev);
    };

    const handleLanguageSelect = (language: typeof currentLanguage) => {
        if (language) {
            setLanguage(language);
        }
        setIsOpen(false);
        setIsActive(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && currentLanguage !== null) {
                setIsOpen(false);
                setIsActive(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={`language-selector ${className}`} ref={dropdownRef}>
            <button
                type="button"
                className={`language-selector-button ${isActive ? 'active' : ''}`}
                onClick={toggleDropdown}
                aria-label="Select language"
                ref={buttonRef}
            >
                
                    <>
                        <span>{`[`}</span>
                        <span className="selected-language">{currentLanguage ? currentLanguage.name : ""}</span>
                        <Svg 
                            src={imageLinks.icon.caret} 
                            className={`dropdown-arrow ${isOpen ? 'arrow-up' : 'arrow-down'}`}
                        />
                        <span>{`]`}</span>

                    </>
            </button>
            {isOpen && (
                <div className="language-dropdown">
                    <p className={`language-prompt ${isTransitioning ? 'transitioning' : ''}`}>
                        {languageTexts[currentTextIndex].text}
                    </p>
                    <div
                        className="language-dropdown-wrapper"
                        role="radiogroup"
                        aria-label="Language selection"
                    >
                        {languages.map((language) => {
                            const isSelected = language.code === currentLanguage?.code;
                            return (
                                <div
                                    key={language.code}
                                    className={`language-option ${isSelected ? 'selected' : ''}`}
                                    role="radio"
                                    aria-checked={isSelected}
                                    tabIndex={isSelected ? 0 : -1}
                                    onClick={() => handleLanguageSelect(language)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleLanguageSelect(language);
                                        }
                                    }}
                                    onKeyUp={(e) => {
                                        if (e.key === ' ') {
                                            e.preventDefault();
                                            handleLanguageSelect(language);
                                        }
                                    }}
                                    aria-label={language.name}
                                >
                                    {/* <img
                                        src={imageLinks.icon[language.code as keyof typeof imageLinks.icon]}
                                        alt=""
                                        aria-hidden="true"
                                        className="language-flag"
                                    /> */}
                                    <span>{language.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LanguageSelector;